import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { motion } from "framer-motion";
import BrandArrow from "@/components/BrandArrow";

/* Флагманская ИИчница SHOW вынесена из карточек в полноширинный баннер —
   компонент перенесён дословно с обучающего сайта (wmt-landing, TrustStrip),
   brand-цвета заменены на фирменный #FF5331 сайта. Остальные события — карточки. */

const events = [
  {
    date: "07.10.2026",
    title: "Бизнес на пороге перелома: как подготовиться к новой AI-реальности",
    description: (
      <>
        ИИ меняет экономику компаний, рынок труда и подходы к организации работы. На вебинаре обсудим,
        какие изменения внутри бизнеса уже происходят на основе собственных кейсов <strong>AI-трансформации</strong> и
        как они повлияют на рынок в ближайшие годы
      </>
    ),
    featured: true,
    href: "https://clck.ru/3WCuPd",
    locked: false,
  },
  {
    date: "11.08.2026",
    title: "ИИ в операционной модели бизнеса",
    description:
      "Разберём опыт Equinor, JPMorgan Chase, Coca-Cola, Haier и BYD и покажем, как встроить ИИ в ежедневную работу компании",
    featured: false,
    href: "https://wmt-ai.ru/events/vebinar_1108/",
    locked: false,
  },
  {
    date: "02.07.2026",
    title: "HR и ИИ: быстрый рывок из «сервисной службы» в лидера изменений",
    description:
      "Специальный ивент для HR-лидеров: как ИИ меняет роль HR и превращает функцию в стратегический центр компании",
    featured: false,
    href: "https://wmt-ai.ru/events/hrd020726/",
    locked: false,
  },
];

export default function EventsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const firstCard = container.firstElementChild as HTMLElement | null;
      const gap = Number.parseFloat(window.getComputedStyle(container).columnGap) || 20;
      const cardWidth = (firstCard?.offsetWidth ?? 340) + gap;
      const index = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.max(0, Math.min(index, events.length - 1)));
      setCanScrollLeft(scrollLeft > 2);
      setCanScrollRight(scrollLeft < container.scrollWidth - container.clientWidth - 2);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const scrollToIndex = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const firstCard = container.firstElementChild as HTMLElement | null;
    const gap = Number.parseFloat(window.getComputedStyle(container).columnGap) || 20;
    const cardWidth = (firstCard?.offsetWidth ?? 340) + gap;
    container.scrollTo({
      left: index * cardWidth,
      behavior: "smooth",
    });
  };

  const scrollByCard = (direction: -1 | 1) => {
    const container = scrollRef.current;
    if (!container) return;
    const firstCard = container.firstElementChild as HTMLElement | null;
    const gap = Number.parseFloat(window.getComputedStyle(container).columnGap) || 20;
    const cardWidth = (firstCard?.offsetWidth ?? 340) + gap;
    container.scrollBy({ left: direction * cardWidth, behavior: "smooth" });
  };

  return (
    <section className="relative z-10 overflow-hidden rounded-t-[32px] bg-[#1a1a1a] py-24 sm:rounded-t-[56px] sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Заголовок — сверху, во всю ширину; карточки ниже выровнены по сетке с баннером */}
        <div className="mb-10 flex items-end justify-between gap-6 lg:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <h2 className="font-heading text-[38px] font-medium leading-tight text-white sm:text-[42px]">
              Наши мероприятия
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#b0b0b0] sm:text-lg">
              Объединяем бизнес, ИИ-практиков и лидеров изменений, обсуждаем мировые
              тенденции развития ИИ в собственных экспертных мероприятиях
            </p>
          </motion.div>

          <div className="hidden shrink-0 gap-2 lg:flex" aria-label="Навигация по мероприятиям">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              disabled={!canScrollLeft}
              aria-label="Предыдущее мероприятие"
              className="flex size-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[#FF5331] hover:text-[#FF5331] disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft className="size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              disabled={!canScrollRight}
              aria-label="Следующее мероприятие"
              className="flex size-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[#FF5331] hover:text-[#FF5331] disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight className="size-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Карточки */}
        <div>
            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory lg:snap-none scrollbar-hide"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {events.map((event, index) => {
                const cardContent = (
                  <>
                    <span
                      className={[
                        "text-[13px] mb-4",
                        event.featured ? "text-white/80" : "text-[#a0a0a0]",
                      ].join(" ")}
                    >
                      {event.date}
                    </span>
                    <h3 className="text-[20px] font-medium leading-[1.3] mb-3">
                      {event.title}
                    </h3>
                    <p
                      className={[
                        "text-[14px] leading-[1.5]",
                        event.featured ? "text-white/80" : "text-[#a0a0a0]",
                      ].join(" ")}
                    >
                      {event.description}
                    </p>
                    <div className="mt-auto flex justify-end">
                      {event.locked ? (
                        <Lock
                          className={[
                            "w-5 h-5",
                            event.featured ? "text-white" : "text-[#FF5331]",
                          ].join(" ")}
                        />
                      ) : (
                        <BrandArrow
                          direction="up-right"
                          className={[
                            "w-5 h-5",
                            event.featured ? "text-white" : "text-[#FF5331]",
                          ].join(" ")}
                        />
                      )}
                    </div>
                  </>
                );

                const cardClasses = [
                  "relative flex h-[260px] flex-shrink-0 flex-col rounded-[16px] p-6 snap-start",
                  "w-[340px] lg:w-[calc((100%-20px)/2)]",
                  event.featured
                    ? "bg-[#FF5331] text-white hover:bg-[#e64a2d]"
                    : "bg-[#222222] border border-[#444444] text-white hover:bg-[#2a2a2a]",
                  event.href ? "cursor-pointer" : "",
                ].join(" ");

                return event.href ? (
                  <motion.a
                    key={index}
                    href={event.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={cardClasses}
                  >
                    {cardContent}
                  </motion.a>
                ) : (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={cardClasses}
                  >
                    {cardContent}
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination dots — mobile only */}
            <div className="flex justify-center gap-2 mt-6 lg:hidden">
              {events.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  aria-label={`Перейти к мероприятию ${index + 1}`}
                  className={[
                    "w-2 h-2 rounded-full transition-colors duration-200",
                    activeIndex === index ? "bg-[#FF5331]" : "bg-[#444444]",
                  ].join(" ")}
                />
              ))}
            </div>
          </div>

        {/* ИИчница SHOW — флагманский баннер (перенос 1:1 с wmt-landing/TrustStrip) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-10 lg:mt-12"
        >
          <div className="rounded-[1.5rem] overflow-hidden relative group bg-black">
            {/* Background photo. On mobile — banner at top. On desktop — full background. */}
            <div className="relative md:absolute md:inset-0">
              <img
                src="/events/iichnica-1.jpg"
                alt="ИИчница — конференция WMT AI"
                loading="lazy"
                className="block w-full h-[220px] md:h-full md:absolute md:inset-0 object-cover object-[center_30%]"
              />
              {/* Gradient overlay — solid black for bottom 35% (mobile flush with content), softer fade on desktop */}
              <div className="absolute inset-0 bg-gradient-to-t from-black from-15% via-black/30 to-transparent md:from-black md:from-0% md:via-black/60 md:via-50% md:to-black/10" />
            </div>
            {/* Content. Mobile: flows below image. Desktop: in-flow so it sets the card height; image fills behind via absolute inset-0. */}
            <div className="relative z-10 p-6 md:p-12 md:min-h-[400px] md:flex md:items-end">
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start md:items-end">
                <div className="lg:col-span-1 text-center md:text-left">
                  <img
                    src="/logos/wmtaichnica.svg"
                    alt="ИИЧНИЦА SHOW"
                    className="h-14 md:h-[4.5rem] lg:h-20 w-auto mb-4 mx-auto md:mx-0 active:scale-95 transition-transform"
                  />
                  <p className="text-sm text-white/70 leading-relaxed font-medium">
                    Собственное мероприятие WMT&nbsp;AI для лидеров бизнеса.
                    Проводится дважды в&nbsp;год в&nbsp;Москве.
                  </p>
                </div>
                <div className="lg:col-span-1 text-center md:text-left border-y md:border-y-0 md:border-x border-white/15 py-6 md:py-0 md:px-8">
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-3 leading-tight tracking-tight font-heading">
                    Итоги: от ИИ-игрушек к&nbsp;AI-трансформации
                  </h4>
                  <p className="text-sm text-white/60 leading-relaxed">
                    Живые подкасты, разборы реальных кейсов внедрения, закрытый
                    нетворкинг и&nbsp;инсайты для C-level.
                  </p>
                </div>
                <div className="lg:col-span-1 flex flex-col items-center lg:items-end gap-5">
                  <div className="text-center lg:text-right">
                    <span className="text-[10px] font-bold text-white/50 uppercase tracking-[0.2em] mb-1 block">
                      Прошедшее событие
                    </span>
                    <span className="text-lg font-bold text-white">
                      31 марта 2026, Москва
                    </span>
                    <span className="text-sm font-semibold text-[#FF5331] mt-1 block">
                      500+ участников
                    </span>
                  </div>
                  <a
                    href="https://www.wmtaichnica.ru/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wmt-cta group border border-white/40 text-white hover:border-white hover:bg-white/5"
                  >
                    <span className="optical-center">Посмотреть, как это было</span>
                    <BrandArrow direction="up-right" className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
